import { Music2 } from 'lucide-react';
import type { CSSProperties, ReactElement } from 'react';

import Image from '@/components/ui/image';
import { useAnimeCover } from '@/hooks/use-anime-cover';

/**
 * Color palette keyed off the kara's songtype. The gradient is the
 * fallback artwork — when an anime cover resolves from Yozora it
 * takes over and the gradient stays behind it as the loading shim.
 */
const KIND_GRADIENT: Record<string, [string, string]> = {
  OP: ['#22d3ee', '#0891b2'],
  ED: ['#c084fc', '#7c3aed'],
  IN: ['#fbbf24', '#d97706'],
  AMV: ['#38bdf8', '#0369a1'],
  OST: ['#34d399', '#047857'],
  MV: ['#f472b6', '#be185d'],
  COVER: ['#fb7185', '#be123c'],
  AUDIO: ['#94a3b8', '#475569'],
};

function gradientCss(kind: string): string {
  const palette = KIND_GRADIENT[kind] ?? KIND_GRADIENT.AUDIO;
  return `linear-gradient(135deg, ${palette[0]}, ${palette[1]})`;
}

export function getKaraGradient(kind: string): string {
  return gradientCss(kind);
}

interface KaraArtworkProps {
  kind: string;
  /** Kara's AniList id, when known — drives the Yozora cover lookup. */
  animeId?: number | null;
  /** Series name from kara.moe — fallback search key when no anilist id. */
  animeName?: string | null;
  /** Optional kind+sequence overlay label. */
  badge?: string;
  /** Override sizing via Tailwind classes. */
  className?: string;
  rounded?: 'lg' | 'xl' | '2xl' | 'full';
  /** Image hint for next/image-style sizing. */
  sizes?: string;
}

/**
 * Square artwork tile. Resolves the kara's anime cover via Yozora
 * (`anilist_ids[0]` → `/api/anime/:id`, fallback search by series name);
 * falls back to a kind-driven gradient with the music-note icon while
 * the cover loads or when no anime can be matched.
 *
 * The icon and badge auto-scale via the container size so one component
 * covers sticky-bar thumbnails (44px), queue rows (40px), and immersive
 * hero (~320px).
 */
export function KaraArtwork({
  kind,
  animeId,
  animeName,
  badge,
  className = 'h-11 w-11',
  rounded = 'lg',
  sizes,
}: KaraArtworkProps): ReactElement {
  const cover = useAnimeCover(animeId ?? null, animeName ?? null);
  const style: CSSProperties = { background: gradientCss(kind) };
  const roundedClass =
    rounded === 'full'
      ? 'rounded-full'
      : rounded === '2xl'
        ? 'rounded-2xl'
        : rounded === 'xl'
          ? 'rounded-xl'
          : 'rounded-lg';

  return (
    <div
      className={`relative flex shrink-0 items-center justify-center overflow-hidden border border-border/40 shadow-sm ${roundedClass} ${className}`}
      style={style}
    >
      {cover?.poster ? (
        <Image
          src={cover.poster}
          alt={cover.title || animeName || ''}
          fill
          sizes={sizes ?? '120px'}
          className="object-cover"
          unoptimized
        />
      ) : (
        <Music2 className="h-1/3 w-1/3 text-white/85" />
      )}
      {badge ? (
        <span className="absolute bottom-1 left-1 rounded-full bg-black/55 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white backdrop-blur-sm">
          {badge}
        </span>
      ) : null}
    </div>
  );
}

export default KaraArtwork;
